import { useEffect, useMemo, useState } from "react";
import {
  useGetOrganizationsQuery,
  useGetTrashQuery,
  useRestoreTrashItemMutation,
  usePurgeTrashItemMutation,
  useEmptyTrashMutation,
} from "../../redux/api";
import type { TrashItem } from "../../redux/api";
import { IconRotateCcw, IconTrash } from "../../components/icons";
import { RowMenu } from "../../components/RowMenu";

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString() : "—";

// Settings → Trash. Lists soft-deleted records for the selected organization —
// restore brings a record back, purge removes it permanently.
const Trash = () => {
  const { data: organizations = [] } = useGetOrganizationsQuery();
  const [orgId, setOrgId] = useState<string>("");

  useEffect(() => {
    if (!orgId && organizations.length) setOrgId(organizations[0].id);
  }, [organizations, orgId]);

  const {
    data: items = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetTrashQuery(orgId || undefined, { skip: !orgId });

  const [restoreTrashItem, { isLoading: restoring }] =
    useRestoreTrashItemMutation();
  const [purgeTrashItem, { isLoading: purging }] = usePurgeTrashItemMutation();
  const [emptyTrash, { isLoading: emptying }] = useEmptyTrashMutation();

  const [typeFilter, setTypeFilter] = useState<string>("");

  const types = useMemo(
    () => [...new Set(items.map((i) => i.type))].sort(),
    [items],
  );

  const visible = useMemo(
    () => (typeFilter ? items.filter((i) => i.type === typeFilter) : items),
    [items, typeFilter],
  );

  const busy = restoring || purging || emptying;

  const handleEmptyTrash = async () => {
    if (!orgId) return;
    if (
      !window.confirm(
        `Permanently delete all ${items.length} item(s) in trash? This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await emptyTrash(orgId).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to empty trash.",
      );
    }
  };

  const handleRestore = async (item: TrashItem) => {
    try {
      await restoreTrashItem({ type: item.type, id: item.id }).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to restore item.",
      );
    }
  };

  const handlePurge = async (item: TrashItem) => {
    if (
      !window.confirm(
        `Permanently delete "${item.name}"? This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await purgeTrashItem({ type: item.type, id: item.id }).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to purge item.",
      );
    }
  };

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load trash"
    : null;

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Trash</h2>
            <p className="panel__sub">
              Showing {visible.length} of {items.length}
            </p>
          </div>
          <div className="toolbar">
            {organizations.length > 1 && (
              <select
                className="select"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
              >
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            )}
            <select
              className="select"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">All types</option>
              {types.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching || !orgId}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn--danger"
              onClick={handleEmptyTrash}
              disabled={busy || !orgId || items.length === 0}
            >
              {emptying ? "Emptying…" : "Empty trash"}
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Deleted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!orgId ? (
                <tr>
                  <td className="data-table__empty" colSpan={4}>
                    Select an organization to view its trash.
                  </td>
                </tr>
              ) : isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={4}>
                    Loading trash…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={4}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={4}>
                    Trash is empty.
                  </td>
                </tr>
              ) : (
                visible.map((item) => (
                  <tr key={`${item.type}:${item.id}`}>
                    <td className="cell-title">{item.name}</td>
                    <td>
                      <span className="pill pill--neutral">{item.label}</span>
                    </td>
                    <td className="cell-date">{formatDate(item.deletedAt)}</td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            label: "Restore",
                            icon: <IconRotateCcw size={16} />,
                            onSelect: () => handleRestore(item),
                            disabled: busy,
                          },
                          {
                            label: "Delete forever",
                            icon: <IconTrash size={16} />,
                            onSelect: () => handlePurge(item),
                            danger: true,
                            disabled: busy,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default Trash;
